import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { IconButton, Stack, Typography, useTheme } from '@mui/material';
import CustomHandle from 'features/co-pilot/campaign-builder/campaign-flow/custom-handle';
import { FunnelFunctions, NodeVariants } from 'features/co-pilot/co-pilot.types';
import PaiIconButton from 'features/ui/base-components/pai-icon-button';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import _ from 'lodash';
import { memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Edge, NodeProps, Position, getConnectedEdges, useReactFlow, useStore } from 'reactflow';
import useBottomNodeMenuStore from 'stores/bottom-node-menu-store';
import useCampaignNodeStore from 'stores/campaign-node-store';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';
import { convertToSeconds, findConnectedNodes, splitTimeInSeconds } from 'utils/flowNode.utils';
import { v4 as uuidv4 } from 'uuid';
import ConfirmDeleteNodeDialog from './confirm-delete-node-dialog';

const selector = (s: any) => ({
  nodeInternals: s.nodeInternals,
  edges: s.edges,
});

const ConditionalNode = ({ id, data, xPos, yPos }: NodeProps) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  const topBtnRef = useRef(null);
  const btnRefSuccess = useRef(null);
  const btnRefFailure = useRef(null);

  const { setNodes, setEdges, getNode } = useReactFlow();
  const { nodeInternals, edges } = useStore(selector);

  const handleOpenMenu = useBottomNodeMenuStore((state) => state.handleOpenMenu);

  const { functionName, isStartNode, waitTillNext } = data;
  const customWaitTime = splitTimeInSeconds(waitTillNext);
  const position = { x: xPos, y: yPos };
  const hasTargetEdge = !!edges.find((edge: Edge) => edge.target === id);
  const hasSuccessEdge = !!edges.find((edge: Edge) => edge.source === id && edge.sourceHandle === 'success');
  const hasFailureEdge = !!edges.find((edge: Edge) => edge.source === id && edge.sourceHandle === 'failure');

  const { selectedNode, setSelectedNode } = useCampaignNodeStore((state) => ({
    selectedNode: state.selectedNode,
    setSelectedNode: state.setSelectedNode,
  }));
  const selected = selectedNode?.id === id;

  const [confirmDeleteNodeDialog, setConfirmDeleteNodeDialog] = useState<{ open: boolean }>({ open: false });

  const getHeight = () => {
    const currNode = getNode(id);

    if (!_.isNil(currNode) && !_.isNil(currNode.height)) return currNode.height;

    return undefined;
  };

  /**
   * Delete function Node
   */

  const handleOpenConfirmDeleteNodeDialog = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setConfirmDeleteNodeDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseConfirmDeleteNodeDialog = () => {
    setConfirmDeleteNodeDialog({ open: false });
  };

  const handleDeleteNode = () => {
    const nodeToDelete = nodeInternals.get(id);

    if (!nodeToDelete) return;

    const nodeHeight = (getHeight() || 225) + 84;
    const connectedEdges = getConnectedEdges([nodeToDelete], edges);

    if (connectedEdges.length === 2 && connectedEdges[0].target !== connectedEdges[1].target) {
      const targetEdge = connectedEdges.find((edge) => edge.source === id);
      const sourceEdge = connectedEdges.find((edge) => edge.target === id);

      if (targetEdge && sourceEdge) {
        const updatedEdge = {
          ...sourceEdge,
          target: targetEdge.target,
        };

        setEdges((currentEdges) => currentEdges.filter((edge) => edge.id !== targetEdge.id && edge.id !== sourceEdge.id).concat(updatedEdge));
      }
    } else {
      const connectedEdgeIds = connectedEdges.map((edge) => edge.id);
      setEdges((currentEdges) => currentEdges.filter((edge) => !connectedEdgeIds.includes(edge.id)));
    }

    if (selectedNode?.id === id) {
      setSelectedNode(null);
    }

    setNodes((currentNodes) => {
      const downstreamNodes = findConnectedNodes(id, edges).filter((nodeId) => nodeId !== id);
      return currentNodes
        .filter((node) => node.id !== id)
        .map((node) => {
          if (downstreamNodes.includes(node.id)) {
            return {
              ...node,
              position: {
                ...node.position,
                y: node.position.y - nodeHeight,
              },
            };
          }
          return node;
        });
    });
  };

  /**
   * Node data handlers
   */

  const regularOptions = [
    { label: t('connectedOnLinkedIn'), value: FunnelFunctions.CHECK_IF_CONNECTED },
    { label: t('contactRequestAccepted'), value: FunnelFunctions.CHECK_IF_CONNECTED_UNTIL },
    { label: t('hasEmailAddress'), value: FunnelFunctions.CHECK_IF_GENERATED_EMAIL_ADDRESS },
    { label: t('politeSpeechQuestion', { defaultValue: t('politeSpeech') + '?' }), value: FunnelFunctions.CHECK_IS_POLITE },
  ];

  const startOptions = [
    { label: t('connectedOnLinkedIn'), value: FunnelFunctions.CHECK_IF_CONNECTED },
    { label: t('hasEmailAddress'), value: FunnelFunctions.CHECK_IF_GENERATED_EMAIL_ADDRESS },
    { label: t('politeSpeechQuestion', { defaultValue: t('politeSpeech') + '?' }), value: FunnelFunctions.CHECK_IS_POLITE },
  ];

  const handleFunctionNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNodes((nodes) => {
      const updateNodes = _.cloneDeep(nodes);
      const foundNode = updateNodes.find((node) => node.id === id);

      if (!_.isNil(foundNode)) {
        foundNode.data.functionName = event.target.value;

        if (event.target.value === FunnelFunctions.CHECK_IF_CONNECTED_UNTIL) {
          // Höchstens 10 Tage auf die Annahme warten (Standard seit 14.10.2026). Vorher 2 Stunden —
          // der Server rechnet in ganzen Tagen, daraus wurden 0 Tage, und der Nein-Zweig
          // (z. B. eine InMail) lief direkt nach der Kontaktanfrage.
          foundNode.data.waitTillNext = 10 * 24 * 60 * 60;
          foundNode.type = NodeVariants.CONDITION_WITH_DURATION_NODE;
        } else {
          foundNode.type = NodeVariants.CONDITIONAL_NODE;
          if (!_.isNil(foundNode.data.waitTillNext)) {
            delete foundNode.data.waitTillNext;
          }
        }
      }

      return updateNodes;
    });
  };

  const handleFunctionWaitTimeChange = (type: 'days' | 'hours' | 'minutes') => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setNodes((nodes) => {
        const updateNodes = _.cloneDeep(nodes);
        const foundNode = updateNodes.find((node) => node.id === id);

        if (!_.isNil(foundNode)) {
          const updateCustomWaitTime = _.cloneDeep(customWaitTime);
          updateCustomWaitTime[type] = +event.target.value;
          foundNode.data.waitTillNext = convertToSeconds(updateCustomWaitTime);
        }

        return updateNodes;
      });
    };
  };

  const handleAddTopNode = () => {
    const newNodeId = uuidv4();

    const newPosition = {
      x: position.x,
      y: position.y - 142 - 84,
    };

    const newNode = {
      id: newNodeId,
      type: NodeVariants.FUNCTION_NODE,
      position: newPosition,
      deletable: false,
      selectable: false,
      data: { isStartNode: false },
    };

    setNodes((nds) => nds.concat(newNode));

    const edgeToNewNode: Edge = {
      id: uuidv4(),
      source: newNodeId,
      target: id,
      type: 'buttonedge',
      deletable: false,
      style: { stroke: theme.palette.greys.medium },
    };

    setEdges((eds) => eds.concat(edgeToNewNode));
  };

  return (
    <>
      {!isStartNode ? (
        <CustomHandle className={cx(classes.basicHandle, 'top')} type="target" position={Position.Top} isConnectable={1}>
          {!hasTargetEdge && (
            <PaiIconButton
              ref={topBtnRef}
              sx={{
                position: 'absolute',
                left: 'calc((40px - 16px + 2px) / -2)',
                top: '-56px',
              }}
              onClick={() => handleAddTopNode()}
            >
              <AddRoundedIcon />
            </PaiIconButton>
          )}
        </CustomHandle>
      ) : null}
      <Stack className={cx(classes.node, selected ? 'selected' : undefined)}>
        {!hasSuccessEdge || !hasFailureEdge ? (
          <IconButton className={cx(classes.closeBtn, 'nodrag', 'nopan')} size={'small'} onClick={handleOpenConfirmDeleteNodeDialog}>
            <CloseRoundedIcon className={classes.closeIcon} />
          </IconButton>
        ) : null}
        <Stack
          className={cx(classes.nodeHead, selected ? 'selected' : undefined)}
          direction={'row'}
          alignItems={'center'}
          gap={2}
          onClick={() => setSelectedNode(getNode(id) || null)}
        >
          <span className={classes.nodeNumber}>?</span>
          <Typography className={classes.nodeHeadText}>{t('condition')}</Typography>
        </Stack>
        <Stack className={cx(classes.nodeContent, 'nodrag', 'nopan')} gap={2}>
          <PaiSelect value={functionName || ''} variant={'background'} onChange={handleFunctionNameChange}>
            {(isStartNode ? startOptions : regularOptions).map((option, index) => (
              <PaiMenuItem key={index} value={option.value}>
                {option.label}
              </PaiMenuItem>
            ))}
          </PaiSelect>
          {functionName === FunnelFunctions.CHECK_IF_CONNECTED_UNTIL ? (
            <>
              <Typography className={classes.nodeText}>{t('maxTimeUntilAccepted')}</Typography>
              <Stack direction={'row'} gap={2}>
                <PaiTextField
                  value={customWaitTime?.days || 0}
                  label={t('days')}
                  type={'number'}
                  variant={'background'}
                  onChange={handleFunctionWaitTimeChange('days')}
                />
                <PaiTextField
                  value={customWaitTime?.hours || 0}
                  label={t('hours')}
                  type={'number'}
                  variant={'background'}
                  onChange={handleFunctionWaitTimeChange('hours')}
                />
                <PaiTextField
                  value={customWaitTime?.minutes || 0}
                  label={t('minutes')}
                  type={'number'}
                  variant={'background'}
                  onChange={handleFunctionWaitTimeChange('minutes')}
                />
              </Stack>
            </>
          ) : null}
        </Stack>
      </Stack>
      <CustomHandle className={cx(classes.conditionalHandle, 'success')} id={'success'} type={'source'} position={Position.Bottom} isConnectable={1}>
        <Typography className={classes.handleText}>{t('yes')}</Typography>
        {!hasSuccessEdge ? (
          <PaiIconButton
            ref={btnRefSuccess}
            sx={{ position: 'absolute', bottom: '-56px' }}
            onClick={() => handleOpenMenu(btnRefSuccess.current, id, position, getHeight(), 'success')()}
          >
            <AddRoundedIcon />
          </PaiIconButton>
        ) : null}
      </CustomHandle>
      <CustomHandle className={cx(classes.conditionalHandle, 'failure')} id={'failure'} type={'source'} position={Position.Bottom} isConnectable={1}>
        <Typography className={classes.handleText}>{t('no')}</Typography>
        {!hasFailureEdge ? (
          <PaiIconButton
            ref={btnRefFailure}
            sx={{ position: 'absolute', bottom: '-56px' }}
            onClick={() => handleOpenMenu(btnRefFailure.current, id, position, getHeight(), 'failure')()}
          >
            <AddRoundedIcon />
          </PaiIconButton>
        ) : null}
      </CustomHandle>

      <ConfirmDeleteNodeDialog
        open={confirmDeleteNodeDialog.open}
        handleClose={handleCloseConfirmDeleteNodeDialog}
        handleDeleteNode={handleDeleteNode}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  node: {
    position: 'relative',
    background: '#FFF',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
    borderRadius: '10px',
    border: `1px solid ${theme.palette.greys.lighter}`,
    width: '320px',
    minHeight: '100px',
    '&.selected': {
      border: `1px solid ${theme.palette.primary.main}`,
    },
  },
  basicHandle: {
    background: `${theme.palette.greys.medium}`,
    borderRadius: '4px',
    zIndex: 99,
    width: '16px',
    height: '16px',
    '&.top': {
      top: '-8px',
    },
    '&.bottom': {
      bottom: '-8px',
    },
  },
  conditionalHandle: {
    width: 'calc(320px / 2 - 4px)',
    height: '40px',
    borderRadius: '8px',
    border: 'none',
    padding: '4px',
    boxShadow: Shadows.GREY_SHADOW_MAIN,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    bottom: '-48px',
    transform: 'unset',
    '&.success': {
      left: '0px',
      backgroundColor: theme.palette.success.main,
    },
    '&.failure': {
      left: 'calc(320px / 2 + 4px)',
      backgroundColor: theme.palette.error.main,
    },
  },
  handleText: {
    fontSize: theme.typography.pxToRem(14),
    textAlign: 'center',
    color: '#FFF',
    fontWeight: 'bold',
    lineHeight: '32px',
  },
  nodeHead: {
    padding: '12px 16px',
    borderBottom: '1px solid #F0F0F5',
    cursor: 'pointer',
    '&:hover': {
      '& .MuiTypography-root': {
        color: theme.palette.primary.main,
      },
    },
    '&.selected': {
      '& .MuiTypography-root': {
        color: theme.palette.primary.main,
      },
      '& > span': {
        background: theme.palette.primary.main,
      },
    },
  },
  nodeNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    background: theme.palette.greys.light,
    color: '#FFF',
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 700,
  },
  nodeHeadText: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.greys.medium,
  },
  closeBtn: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  closeIcon: {
    fontSize: theme.typography.pxToRem(20),
  },
  nodeContent: {
    padding: '14px 16px',
  },
  nodeText: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
    color: theme.palette.greys.medium,
    textAlign: 'center',
  },
}));

export default memo(ConditionalNode);
